import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Tools } from 'src/app/classes/shared/tools';
import { ChartHandlerV2 } from 'src/app/classes/widget/chartHandlerV2';
import { WidgetLoadingComponent } from 'src/app/components/project-pages/dashboard/widget-loading/widget-loading.component';
import { WidgetTitleComponent } from 'src/app/components/project-pages/dashboard/widget-title/widget-title.component';
import { WidgetDirective } from 'src/app/components/project-pages/dashboard/widgets/widget.directive';

// Display of a column, in widget.design.columns[<column name>]; absent = text.
export type TableColumnFormat = 'text' | 'chip' | 'progress';
export type ChipTone = 'good' | 'warning' | 'critical' | 'neutral';

export interface TableColumnDesign {
  format: TableColumnFormat;
  warning?: number;           // chip: thresholds
  critical?: number;
  higherIsWorse?: boolean;    // chip: default true
  max?: number;               // progress: 100 %, the column maximum if absent
}

// A cell as shown, computed once per data load (the template only reads it).
export interface TableCell {
  value: string;
  format: TableColumnFormat | 'project';
  numeric: boolean;
  tone?: ChipTone;
  percent?: number;
}

export interface TableHeader {
  name: string;
  numeric: boolean;
}

// Column displays and total row: on once set (design.columnsEnabled, or older widgets with settings), off by default.
export function tableOptionsEnabled(design: any): boolean {
  return design?.columnsEnabled ?? !!(Object.keys(design?.columns ?? {}).length || design?.total);
}

const stripPayload = (key: string): string => key.startsWith("payload.") ? key.substring(8) : key;

// Columns of a table from its query: X fields, then the aggregations (shared with the editor).
export function tableColumns(query: any): { xKeys: string[]; yKeys: string[]; columns: string[] } {
  const xKeys = Object.keys(query?.field ?? {}).map(stripPayload);
  const yKeys = Object.keys(query?.aggregation ?? {}).map(stripPayload);
  return { xKeys, yKeys, columns: new Tools().renameDuplicates([...xKeys, ...yKeys]) };
}

// Chip color from the thresholds: past `critical` then past `warning` is bad (below them if lower is worse).
const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  return typeof value === 'string' && value.trim() !== '' ? Number(value) : Number.NaN;
};

export function chipTone(value: unknown, column: TableColumnDesign): ChipTone {
  const number = toNumber(value);
  if (!Number.isFinite(number) || column.warning == null || column.critical == null) return 'neutral';

  const worse = column.higherIsWorse !== false;
  if (worse ? number >= column.critical : number <= column.critical) return 'critical';
  if (worse ? number >= column.warning : number <= column.warning) return 'warning';
  return 'good';
}

export function progressPercent(value: unknown, max: number | undefined): number {
  const number = Number(value);
  if (!Number.isFinite(number) || !max || max <= 0) return 0;
  return Math.min(100, Math.max(0, number / max * 100));
}

@Component({
  selector: 'app-table',
  imports: [
    WidgetLoadingComponent,
    WidgetTitleComponent,
    CommonModule
  ],
  standalone: true,
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss'
})

export class TableComponent extends WidgetDirective {
  // Services specific to TableComponent
  private readonly tools: Tools = new Tools();
  private readonly translate: TranslateService = inject(TranslateService);

  // Variables common to TableComponent
  containerStyle: string = "";
  blockStyle: string = "width:100%;display:flex;flex-direction:column;";
  titleStyle: string = "font-size:16px;margin:0px 20px 20px;";
  titlePosition: string = "top";
  valueStyle: string = "";

  headers: TableHeader[] = [];
  rows: TableCell[][] = [];
  totals: TableCell[] | null = null;           // design.total: sums of the number columns
  headerStyle: string = "";
  chartHandler: ChartHandlerV2 = new ChartHandlerV2();

  override ngOnInit(): void {
    this.resetStyles();
    super.ngOnInit();
  }

  init() {
    if (!this.widget || !this.widget.visible || this.resize) return;

    this.widgetDesign = this.widget.design;
    this.widgetData = this.widget.data;
    this.widgetQuery = this.widget.query;

    if (this.type !== this.widget.type) {
      this.resetStyles();
    }

    this.type = this.widget.type;
    this.initOptions();

    this.updateTable();
  }

  // Light look by default (see the scss); sizes and alignment only when the design sets them.
  resetStyles() {
    this.blockStyle = "width:100%;display:flex;flex-direction: column;align-content:center;";
    this.titleStyle = "font-size:16px;margin:0px 20px 20px;";
    this.containerStyle = "";
    this.headerStyle = "";
    this.valueStyle = "";
  }

  initOptions() {
    try {
      this.resetStyles();
      let options = this.widgetDesign;

      if (!options) return

      this.containerStyle = `background-color: var(--background-color);`

      if (options.title) {
        this.showTitle = options.title.display == undefined ? true : options.title.display;
        this.titlePosition = options.title.position || 'top';
        this.titleStyle = `font-size:${options.title.fontSize || '16'}px; background-color: var(--background-color); color: var(--text-color); padding:10px 20px; text-align:${options.title.align || 'center'}; margin:0px 0px 0px 0px; border-radius: 15px;`;
        // On the cells (start | center | end): a set alignment wins over the numbers on the right.
        const cellAlign = options.cell?.align ? 'text-align:' + options.cell.align + ';' : '';
        const headerSize = options.header?.valueFontSize ? 'font-size:' + options.header.valueFontSize + 'px;' : '';
        const headerAlign = options.header?.align ? 'text-align:' + options.header.align + ';' : '';
        this.valueStyle = `height:${options.cell?.height || '48'}px;font-size:${options.cell?.valueFontSize || '14'}px;${cellAlign}`;
        this.headerStyle = `height:${options.header?.height || '48'}px;${headerSize}${headerAlign}`;
      }
    } catch (e: any) {
      console.error("Error initializing widget options:", e);
    }
  }

  updateTable() {
    if (this.widgetDesign.yFormat) {
      Object.keys(this.widgetDesign.yFormat).forEach(key => {
        if (this.widgetDesign && this.widgetDesign[key]) {
          this.widgetDesign[key].type = this.widgetDesign.yFormat[key];
        }
      });
    }

    this.headers = [];
    this.rows = [];
    this.totals = null;

    // Any row: a first value of 0 (a count of an absent field) is a row too.
    if (this.widgetData?.length) {
      const { xKeys, yKeys } = tableColumns(this.widgetQuery);
      const design = this.widgetDesign;
      // Options off: plain columns, no total (their settings kept).
      const options = tableOptionsEnabled(design);

      // Synthesis rows comparing projects: their project comes first.
      const hasProject = this.widgetData[0].p !== undefined;
      const names = this.tools.renameDuplicates([...(hasProject ? [this.translate.instant('synthesis.project_column')] : []), ...xKeys, ...yKeys]);
      const keys = [...(hasProject ? ['p'] : []), ...(xKeys[0] !== undefined ? ['x'] : []), ...(xKeys[1] !== undefined ? ['s'] : []), ...yKeys.map((_key, index) => index == 0 ? 'y' : 'y' + index)];
      const yStart = keys.length - yKeys.length;
      const settings = names.map((name, index): TableColumnDesign & { kind: TableCell['format'] } => {
        if (hasProject && index === 0) return { format: 'text', kind: 'project' };
        const column: TableColumnDesign = (options && design.columns?.[name]) || { format: 'text' };
        return { ...column, kind: column.format };
      });
      const max = keys.map(key => Math.max(0, ...this.widgetData.map((item: any) => Number(item[key])).filter(Number.isFinite)));
      const format = (index: number, value: unknown): string => {
        if (index >= yStart) return this.chartHandler.formatValue(design['Y' + (index - yStart + 1)], value ?? '');
        return value != null && value !== '' ? this.chartHandler.formatValue(design.xFormat, value) : '';
      };

      this.headers = names.map((name, index) => ({ name, numeric: index >= yStart }));
      this.rows = this.widgetData.map((item: any) => keys.map((key, index): TableCell => {
        const raw = item[key];
        const column = settings[index]!;
        return {
          value: index === 0 && hasProject ? raw ?? '' : format(index, raw),
          format: column.kind,
          numeric: index >= yStart,
          ...(column.kind === 'chip' && { tone: chipTone(raw, column) }),
          ...(column.kind === 'progress' && { percent: progressPercent(raw, column.max || max[index]) }),
        };
      }));

      if (options && design.total) {
        const total = (key: string, index: number): string => {
          if (index === 0 && yStart > 0) return this.translate.instant('editor.table_total_label');
          return index >= yStart ? format(index, this.widgetData.reduce((sum: number, item: any) => sum + (Number(item[key]) || 0), 0)) : '';
        };
        this.totals = keys.map((key, index): TableCell => ({
          value: total(key, index),
          format: 'text',
          numeric: index >= yStart,
        }));
      }
    }

    this.widgetRendered = true;
  }
}
